import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';

// Import profile components
import RideStats from '../components/profile/RideStats';
import ProfilePhoto from '../components/profile/ProfilePhoto';
import PersonalInfo from '../components/profile/PersonalInfo';
import PaymentMethods from '../components/profile/PaymentMethods';
import SavedAddresses from '../components/profile/SavedAddresses';
import NotificationPreferences from '../components/profile/NotificationPreferences';

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    homeAddress: '',
  });
  
  // Payment methods state
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showAddWalletModal, setShowAddWalletModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [cardForm, setCardForm] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
  });
  const [walletForm, setWalletForm] = useState({
    walletType: 'paypal',
    email: '',
    phoneNumber: '',
  });
  
  // Profile photo state
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Saved addresses state
  const [savedAddresses, setSavedAddresses] = useState({
    home: '',
    work: '',
    favorites: []
  });
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState({
    label: '',
    address: ''
  });
  const [editingAddressType, setEditingAddressType] = useState(null);

  // Fetch user profile on mount
  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    const fetchUserProfile = async () => {
      try {
        const api = (await import('../utils/api')).default;
        const response = await api.get('/auth/me');
        const userData = response.data.user;
        
        setUser(userData);
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          homeAddress: userData.homeAddress || '',
        });
        
        // Update localStorage
        localStorage.setItem('tekstar_user', JSON.stringify(userData));
      } catch (error) {
        console.error('Error fetching profile:', error);
        navigate('/login');
      }
    };
    
    fetchUserProfile();
  }, [navigate]);

  // Save profile handler
  const handleSave = async () => {
    try {
      const api = (await import('../utils/api')).default;
      await api.put('/auth/profile', {
        name: formData.name,
        phone: formData.phone,
        homeAddress: formData.homeAddress,
      });
      
      // Fetch updated user data
      const response = await api.get('/auth/me');
      const userData = response.data.user;
      
      setUser(userData);
      localStorage.setItem('tekstar_user', JSON.stringify(userData));
      setEditMode(false);
      
      toast({
        title: 'Profile updated',
        description: 'Your profile has been saved successfully',
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to update profile. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Load payment methods from localStorage
  useEffect(() => {
    const savedMethods = localStorage.getItem('payment_methods');
    if (savedMethods) {
      setPaymentMethods(JSON.parse(savedMethods));
    }
  }, []);

  // Load saved addresses when user data loads
  useEffect(() => {
    if (user && user.savedAddresses) {
      setSavedAddresses(user.savedAddresses);
    }
  }, [user]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Profile</h1>
          <p className="text-gray-600">Manage your account information</p>
        </div>

        <div className="space-y-6">
          {/* Personal Information Card */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
              {!editMode ? (
                <Button onClick={() => setEditMode(true)} variant="outline">
                  Edit Profile
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    onClick={() => {
                      setEditMode(false);
                      // Reset form data
                      setFormData({
                        name: user.name || '',
                        email: user.email || '',
                        phone: user.phone || '',
                        homeAddress: user.homeAddress || '',
                      });
                    }} 
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} className="bg-pink-600 hover:bg-pink-700">
                    Save Changes
                  </Button>
                </div>
              )}
            </div>

            {/* Profile Photo Component */}
            <ProfilePhoto 
              user={user}
              profilePhoto={profilePhoto}
              setProfilePhoto={setProfilePhoto}
              setUser={setUser}
              formData={formData}
              uploadingPhoto={uploadingPhoto}
              setUploadingPhoto={setUploadingPhoto}
            />

            {/* Personal Info Component */}
            <PersonalInfo 
              formData={formData}
              setFormData={setFormData}
              editMode={editMode}
            />
          </Card>

          {/* Payment Methods Component */}
          <PaymentMethods 
            paymentMethods={paymentMethods}
            setPaymentMethods={setPaymentMethods}
            showAddCardModal={showAddCardModal}
            setShowAddCardModal={setShowAddCardModal}
            showAddWalletModal={showAddWalletModal}
            setShowAddWalletModal={setShowAddWalletModal}
            showDeleteConfirm={showDeleteConfirm}
            setShowDeleteConfirm={setShowDeleteConfirm}
            cardToDelete={cardToDelete}
            setCardToDelete={setCardToDelete}
            cardForm={cardForm}
            setCardForm={setCardForm}
            walletForm={walletForm}
            setWalletForm={setWalletForm}
          />

          {/* Saved Addresses Component */}
          <SavedAddresses 
            savedAddresses={savedAddresses}
            setSavedAddresses={setSavedAddresses}
            showAddAddressModal={showAddAddressModal}
            setShowAddAddressModal={setShowAddAddressModal}
            addressForm={addressForm}
            setAddressForm={setAddressForm}
            editingAddressType={editingAddressType}
            setEditingAddressType={setEditingAddressType}
            user={user}
            setUser={setUser}
          />

          {/* Notification Preferences Component */}
          <NotificationPreferences />

          {/* Ride Statistics Component */}
          <RideStats />
        </div>
      </div>
    </div>
  );
};

export default Profile;
